import type { Response } from "../types";
export type ApiMethod<TResponse = unknown> = (params: {
  timeout?: number;
  limit?: number;
  state?: string;
  keys: string[][];
}) => Promise<Response & { state?: string; events?: TResponse[] }>;
export type ResponseCallback<TResponse> = (response: Response & { state?: string; events?: TResponse[] }) => void;
export type ErrorCallback = (error: Error) => void;

export interface PollingManagerConfig {
  timeout?: number;
  autoReconnect?: boolean;
  reconnectDelay?: number;
  maxReconnectAttempts?: number;
}

export class PollingManager<TResponse = unknown> {
  protected _state?: string | null = null;
  protected _keys: string[][] = [];
  private _isRunning = false;
  private _reconnectAttempts = 0;
  private _config: Required<PollingManagerConfig>;
  private _abortController?: AbortController;
  protected _apiMethod?: ApiMethod<TResponse>;
  private _onResponse?: ResponseCallback<TResponse>;
  private _onError?: ErrorCallback;

  constructor(config: PollingManagerConfig = {}, apiMethod: ApiMethod<TResponse>) {
    this._config = {
      timeout: config.timeout || 30000,
      autoReconnect: config.autoReconnect ?? true,
      reconnectDelay: config.reconnectDelay || 1000,
      maxReconnectAttempts: config.maxReconnectAttempts,
    };
    this._apiMethod = apiMethod;
  }

  setResponseCallback(callback: ResponseCallback<TResponse>) {
    this._onResponse = callback;
  }

  setErrorCallback(callback: ErrorCallback) {
    this._onError = callback;
  }

  addKeys(keys: string[]) {
    this._keys.push(keys);
  }

  async start(): Promise<void> {
    if (this._isRunning) {
      console.warn("PollingManager is already running");
      return;
    }

    if (!this._apiMethod) {
      console.error("API method is not set. Use setMethod() or pass it in constructor.");
      return;
    }

    if (this._keys.length === 0) {
      console.error("Keys are not set. Use setKeys() to add keys.");
      return;
    }

    if (this._state === null) {
      console.info("initial state");

      await this._initial();
    }

    this._isRunning = true;
    this._reconnectAttempts = 0;

    this._startPolling();
  }

  stop(): void {
    this._isRunning = false;
    this._abortController?.abort();
    this._abortController = undefined;
    this._state = null;
  }

  updateConfig(config: Partial<PollingManagerConfig>): void {
    this._config = { ...this._config, ...config };
  }

  get isRunning(): boolean {
    return this._isRunning;
  }

  private async _initial(): Promise<void> {
    if (!this._apiMethod) return;

    try {
      const response = await this._apiMethod({ keys: this._keys });

      this._state = response.state;
    } catch (error) {
      console.error("Failed to initialize state:", error);
      throw error;
    }
  }

  private async _startPolling(): Promise<void> {
    if (!this._isRunning || !this._apiMethod || this._state === null) return;

    try {
      this._abortController = new AbortController();

      const response = await this._apiMethod({ state: this._state, keys: this._keys });

      this._state = response.state;

      if (this._onResponse) {
        this._onResponse(response);
      }

      this._reconnectAttempts = 0;

      if (this._isRunning && this._config.autoReconnect) {
        setTimeout(() => {
          this._startPolling();
        }, 100);
      }
    } catch (error) {
      if (!this._isRunning) return;

      console.error("Polling error:", error);

      if (this._onError) {
        this._onError(error as Error);
      }

      if (this._isRunning && this._config.autoReconnect) {
        this._handleReconnect();
      }
    }
  }

  private _handleReconnect(): void {
    if (!this._isRunning) return;

    this._reconnectAttempts++;

    if (this._config.maxReconnectAttempts && this._reconnectAttempts > this._config.maxReconnectAttempts) {
      console.error("Max reconnection attempts reached");
      this.stop();

      return;
    }

    console.log(
      `Reconnecting... attempt ${this._reconnectAttempts}${this._config.maxReconnectAttempts ? `/${this._config.maxReconnectAttempts}` : ""}`,
    );

    // Экспоненциальная задержка с jitter
    const delay = Math.min(
      this._config.reconnectDelay * Math.pow(2, this._reconnectAttempts - 1) + Math.random() * 1000,
      30000, // максимум 30 секунд
    );

    setTimeout(() => {
      if (this._isRunning) {
        this._startPolling();
      }
    }, delay);
  }
}
